import React, { useMemo, useRef } from 'react';

import PropTypes from 'prop-types';

const KEYWORDS = /\b(SELECT|FROM|WHERE|AND|OR|NOT|NULL|IS|IN|LIKE|BETWEEN|JOIN|LEFT|RIGHT|INNER|OUTER|FULL|ON|AS|GROUP|BY|ORDER|HAVING|LIMIT|OFFSET|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|ALTER|DROP|TABLE|VIEW|INDEX|DISTINCT|CASE|WHEN|THEN|ELSE|END|UNION|ALL|EXISTS|DESC|ASC)\b/gi;
const CONSTANTS = /\b(TRUE|FALSE|CURRENT_DATE|CURRENT_TIME|CURRENT_TIMESTAMP|NOW\(\)|CURDATE\(\)|CURTIME\(\))\b/gi;
const NUMBERS = /\b\d+(\.\d+)?\b/g;

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function highlightSql(value) {
  const placeholders = [];
  let html = escapeHtml(value);

  const stash = (className, content) => {
    const token = `__SQL_TOKEN_${placeholders.length}__`;
    placeholders.push({
      token,
      html: `<span class="${className}">${content}</span>`,
    });
    return token;
  };

  html = html.replace(/(--[^\n]*|#[^\n]*)/g, (match) => stash('sql-editor__comment', match));
  html = html.replace(/(\/\*[\s\S]*?\*\/)/g, (match) => stash('sql-editor__comment', match));
  html = html.replace(/(`[^`]*`)/g, (match) => stash('sql-editor__identifier', match));
  html = html.replace(/('(?:''|[^'])*')/g, (match) => stash('sql-editor__string', match));

  html = html.replace(KEYWORDS, '<span class="sql-editor__keyword">$1</span>');
  html = html.replace(CONSTANTS, '<span class="sql-editor__constant">$1</span>');
  html = html.replace(NUMBERS, '<span class="sql-editor__number">$&</span>');

  placeholders.forEach(({ token, html: replacement }) => {
    html = html.replace(token, replacement);
  });

  return html;
}

function SqlEditor({
  value,
  onChange,
  rows,
  placeholder,
  invalid,
}) {
  const textareaRef = useRef(null);
  const highlightRef = useRef(null);

  const highlighted = useMemo(() => highlightSql(value || ''), [value]);

  const syncScroll = () => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  return (
    <>
      <style>
        {`
          .sql-editor {
            position: relative;
            border: 1px solid #ced4da;
            border-radius: 0.375rem;
            background: #f8fafc;
            color: #1f2937;
            overflow: hidden;
          }

          .sql-editor:focus-within {
            border-color: #86b7fe;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
          }

          .sql-editor__highlight,
          .sql-editor__input {
            margin: 0;
            padding: 1rem;
            min-height: 28rem;
            font-family: Consolas, Monaco, "Courier New", monospace;
            font-size: 0.92rem;
            line-height: 1.45;
            white-space: pre;
            overflow: auto;
            tab-size: 2;
          }

          .sql-editor__highlight {
            color: #1f2937;
            background: transparent;
            pointer-events: none;
          }

          .sql-editor__input {
            position: absolute;
            inset: 0;
            width: 100%;
            resize: vertical;
            border: 0;
            background: transparent;
            color: transparent;
            caret-color: #111827;
            -webkit-text-fill-color: transparent;
            outline: none;
          }

          .sql-editor__input::placeholder {
            color: #64748b;
            -webkit-text-fill-color: #64748b;
          }

          .sql-editor__keyword {
            color: #9f1239;
            font-weight: 700;
          }

          .sql-editor__constant {
            color: #1d4ed8;
            font-weight: 700;
          }

          .sql-editor__comment {
            color: #64748b;
            font-style: italic;
          }

          .sql-editor__identifier {
            color: #047857;
            font-weight: 700;
          }

          .sql-editor__string {
            color: #b45309;
          }

          .sql-editor__number {
            color: #7c3aed;
          }

          body[data-layout-mode="dark"] .sql-editor {
            border-color: rgba(83, 84, 77, 0.85);
            background: linear-gradient(180deg, rgba(51, 52, 46, 0.98) 0%, rgba(43, 44, 39, 0.98) 100%);
            color: #f3efe4;
          }

          body[data-layout-mode="dark"] .sql-editor:focus-within {
            border-color: rgba(76, 168, 231, 0.72);
            box-shadow: 0 0 0 0.2rem rgba(76, 168, 231, 0.12);
          }

          body[data-layout-mode="dark"] .sql-editor__highlight {
            color: #f3efe4;
            background: transparent;
          }

          body[data-layout-mode="dark"] .sql-editor__input {
            background: transparent;
            caret-color: #f4f1e6;
          }

          body[data-layout-mode="dark"] .sql-editor__input::placeholder {
            color: #b2b0a4;
            -webkit-text-fill-color: #b2b0a4;
          }

          body[data-layout-mode="dark"] .sql-editor__keyword {
            color: #ffe08a;
          }

          body[data-layout-mode="dark"] .sql-editor__constant {
            color: #8fd8ff;
          }

          body[data-layout-mode="dark"] .sql-editor__comment {
            color: #b2b0a4;
          }

          body[data-layout-mode="dark"] .sql-editor__identifier {
            color: #9ee6b3;
          }

          body[data-layout-mode="dark"] .sql-editor__string {
            color: #f7c58b;
          }

          body[data-layout-mode="dark"] .sql-editor__number {
            color: #d7b8ff;
          }
        `}
      </style>
      <div className={`sql-editor ${invalid ? 'is-invalid' : ''}`}>
        <pre
          ref={highlightRef}
          className="sql-editor__highlight"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: `${highlighted}\n` }}
        />
        <textarea
          ref={textareaRef}
          className="sql-editor__input"
          value={value}
          rows={rows}
          spellCheck="false"
          placeholder={placeholder}
          onScroll={syncScroll}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </>
  );
}

SqlEditor.defaultProps = {
  value: '',
  rows: 20,
  placeholder: '',
  invalid: false,
};

SqlEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
};

export default SqlEditor;
